import { useAppSelector } from "@/redux/hooks";
import BackControls from "../components/BackControls";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Track } from "../data/sharedTypes";
import {
    getImDisciplinesForInstitution,
    getImSpecialtiesForInstitutionAndDiscipline,
    type ImDiscipline,
    type ImInstitution,
} from "../data/internalMissionsData";
import StepHeader from "../StepHeader";

interface Props {
    universityNameKey: string;
    institution: ImInstitution;
    track: Track;
    onBack: () => void;
    onSelectDiscipline: (discipline: ImDiscipline) => void;
}

/**
 * Internal Missions — discipline picker (IM-only step).
 *
 * Lists the discipline groupings available for the chosen institution+track.
 * Engineering is automatically excluded under the Literary tab because the
 * data has no engineering rows with track="literary".
 */
export default function ChooseImDiscipline({
    universityNameKey,
    institution,
    track,
    onBack,
    onSelectDiscipline,
}: Props) {
    const { t } = useTranslation();
    const { locale } = useAppSelector((state) => state);

    const disciplines = getImDisciplinesForInstitution(institution.key, track);

    return (
        <div className="flex flex-col gap-y-6">
            <BackControls onBack={onBack} />

            <StepHeader
                title={t("dream_university_im_choose_discipline")}
                subtitle={t(universityNameKey)}
            />

            {/* Selection trail */}
            <div className="flex flex-wrap items-center justify-center gap-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-gray-100 text-gray-700">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white ring-1 ring-gray-200 overflow-hidden text-sm leading-none">
                        {institution.emoji}
                    </span>
                    {t(`dream_university_institution_${institution.key}`)}
                </span>
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ring-1 ${
                    track === "scientific"
                        ? "bg-blue-50 text-blue-700 ring-blue-200"
                        : "bg-purple-50 text-purple-700 ring-purple-200"
                }`}>
                    {t(`dream_university_track_${track}`)}
                </span>
            </div>

            {/* Discipline grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {disciplines.map((discipline) => {
                    const count = getImSpecialtiesForInstitutionAndDiscipline(
                        institution.key,
                        discipline.key,
                        track,
                    ).length;
                    return (
                        <button
                            key={discipline.key}
                            onClick={() => onSelectDiscipline(discipline)}
                            className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:border-theme-500 hover:shadow-md transition-all duration-200 text-start"
                        >
                            <span className="text-2xl shrink-0">{discipline.emoji}</span>
                            <div className="flex flex-col gap-0.5 flex-1">
                                <span className="text-sm font-medium text-gray-800">
                                    {t(`dream_university_im_discipline_${discipline.key}`)}
                                </span>
                                <span className="text-xs text-gray-500">
                                    {t("dream_university_im_specialty_count", { count })}
                                </span>
                            </div>
                        </button>
                    );
                })}
            </div>

            {disciplines.length === 0 && (
                <p className="text-center text-sm text-gray-500 py-6">
                    {t("dream_university_im_no_disciplines_for_track")}
                </p>
            )}
        </div>
    );
}
